import type {
  CanvasSectionBlockLayout,
  HomepageLayoutContent,
} from '../components/sections/sectionRegistry';
import { createPageLayout, listPageLayoutsForAdmin } from './pageLayoutsDraft';
import { getIntroFallbackVariant } from './introPage';
import { DEFAULT_INTRO_VIEWER_PIPELINE_OVERRIDES } from './introViewerDefaults';
import {
  decorativeAssetPresetToBlockContent,
  decorativeAssetPresetToBlockStyle,
  getDecorativeAssetPreset,
} from './decorativeAssets';

export const HOLDING_PAGE_KEY = 'holding';
export const HOLDING_PAGE_TITLE = 'Holding Page';

type HoldingPageComposition = 'desktop' | 'tablet' | 'mobile';

const HOLDING_PAGE_BLOCK_LAYOUTS: Record<
  HoldingPageComposition,
  Record<string, CanvasSectionBlockLayout>
> = {
  desktop: {
    'holding-logo': { x: 2, y: 2, w: 2, h: 1 },
    'holding-eyebrow-divider': { x: 3, y: 5, w: 9, h: 1 },
    'holding-eyebrow': { x: 3, y: 5, w: 9, h: 1 },
    'holding-title': { x: 3, y: 6, w: 9, h: 3 },
    'holding-body': { x: 3, y: 10, w: 7, h: 2 },
    'holding-instagram': { x: 3, y: 13, w: 4, h: 1 },
    'holding-email': { x: 7, y: 13, w: 6, h: 1 },
    'holding-facebook': { x: 13, y: 13, w: 4, h: 1 },
    'holding-footer-icon': { x: 3, y: 17, w: 1, h: 1 },
    'holding-footer': { x: 4, y: 17, w: 11, h: 1 },
    'holding-viewer': { x: 14, y: 2, w: 9, h: 15 },
    'holding-symbol-motif': { x: 6, y: 1, w: 17, h: 10 },
  },
  tablet: {
    'holding-logo': { x: 2, y: 1, w: 2, h: 1 },
    'holding-viewer': { x: 5, y: 1, w: 16, h: 8 },
    'holding-symbol-motif': { x: 2, y: 1, w: 22, h: 9 },
    'holding-eyebrow-divider': { x: 5, y: 8, w: 16, h: 1 },
    'holding-eyebrow': { x: 5, y: 8, w: 16, h: 1 },
    'holding-title': { x: 5, y: 9, w: 16, h: 2 },
    'holding-body': { x: 5, y: 11, w: 16, h: 2 },
    'holding-instagram': { x: 3, y: 14, w: 6, h: 1 },
    'holding-email': { x: 10, y: 14, w: 7, h: 1 },
    'holding-facebook': { x: 18, y: 14, w: 5, h: 1 },
    'holding-footer-icon': { x: 7, y: 18, w: 1, h: 1 },
    'holding-footer': { x: 8, y: 18, w: 14, h: 1 },
  },
  mobile: {
    'holding-symbol-motif': { x: 1, y: 9, w: 24, h: 9 },
    'holding-viewer': { x: 2, y: 9, w: 20, h: 7 },
    'holding-eyebrow-divider': { x: 5, y: 2, w: 14, h: 1 },
    'holding-eyebrow': { x: 5, y: 2, w: 14, h: 1 },
    'holding-title': { x: 3, y: 3, w: 18, h: 3 },
    'holding-body': { x: 4, y: 6, w: 16, h: 3 },
    'holding-logo': { x: 1, y: 22, w: 1, h: 1 },
    'holding-instagram': { x: 3, y: 14, w: 18, h: 1 },
    'holding-email': { x: 3, y: 15, w: 18, h: 1 },
    'holding-facebook': { x: 3, y: 16, w: 18, h: 1 },
    'holding-footer-icon': { x: 3, y: 20, w: 1, h: 1 },
    'holding-footer': { x: 4, y: 20, w: 18, h: 2 },
  },
};

const introVariant = getIntroFallbackVariant();
const holdingSymbolMotifPreset = getDecorativeAssetPreset('symbol-folklore-mixed-placeholder');
const holdingEyebrowDividerPreset = getDecorativeAssetPreset('divider-celestial-dot');
const holdingFooterIconPreset = getDecorativeAssetPreset('corner-celestial-mark');

function holdingResponsiveLayout(blockId: string) {
  return {
    tablet: HOLDING_PAGE_BLOCK_LAYOUTS.tablet[blockId],
    mobile: HOLDING_PAGE_BLOCK_LAYOUTS.mobile[blockId],
  };
}

export const DEFAULT_HOLDING_LAYOUT_CONTENT: HomepageLayoutContent = {
  version: 1,
  flows: [],
  flowState: {},
  sections: [
    {
      type: 'canvas',
      id: 'holding-stage',
      settings: {
        layoutMode: 'composition',
        padding: 'none',
        paddingTop: 'none',
        paddingBottom: 'none',
        maxWidth: 'full',
        background: 'default',
        canvas: {
          minRows: 22,
          presentation: 'immersive',
          blocks: [
            {
              id: 'holding-viewer',
              type: 'model-viewer',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-viewer'],
              responsiveLayout: holdingResponsiveLayout('holding-viewer'),
              content: {
                modelPath: introVariant.modelPath,
                vaseId: introVariant.vaseId,
                modelSelectionMode: 'random',
                colourCombinationSelectionMode: 'random',
                finishType: introVariant.finishType,
                selectedColors: introVariant.selectedColors,
                publicLightingMode: 'hero',
                renderPresetId: 'hero',
                surface: 'immersive',
                viewerBackgroundPreset: 'transparent',
                stageAtmosphere: 'luxury',
                stageGrounding: 'soft-gallery',
                stageFit: 'contain',
                stageMaxWidthPercent: 92,
                stageMaxHeightPercent: 94,
                stageHorizontalAlign: 'center',
                stageVerticalAlign: 'bottom',
                sceneLayerMode: 'section-backdrop',
                sectionSceneHorizonY: 84,
                sectionFloorPipelineDefaults: {
                  floorShape: 'rectangle',
                  floorPlaneMode: 'model-base',
                  floorReflectivity: 0.52,
                  floorEdgeSoftness: 0.44,
                  reflectionForegroundFade: 0.42,
                  reflectionDepthFade: 0.66,
                  reflectionNearBaseSharpness: 0.62,
                  reflectionViewerSideSoftness: 0.5,
                  reflectionVisibility: 0.9,
                  reflectionDepthMultiplier: 1.38,
                  floorSurfaceOpacity: 0.24,
                  floorColor: '#121010',
                  reflectionPlaneYOffset: -0.06,
                  floorSurfacePlaneYOffset: -0.06,
                  reflectionHorizonScreenShift: 0,
                  floorPlaneForwardOffset: 0,
                },
                sectionBacklightEnabled: true,
                sectionBacklightIntensity: 1.34,
                sectionBacklightBlur: 24,
                sectionBacklightWidth: 66,
                sectionBacklightHeight: 26,
                sectionBacklightX: 74,
                sectionBacklightYOffset: 0,
                sectionBacklightCoreIntensity: 1.06,
                sectionBacklightCoreBlur: 7,
                sectionBacklightCoreWidth: 58,
                sectionBacklightCoreHeight: 8,
                sectionBacklightFloorSheenIntensity: 0.72,
                sectionBacklightFloorSheenHeight: 12,
                sectionBacklightEdgeFeather: 18,
                sectionBacklightColourInfluence: 0.55,
                sectionBacklightColorMode: 'colourway',
                sectionBacklightWarmColor: '#d0a04f',
                sectionBacklightCoolColor: '#7b58d6',
                sectionWallLightEnabled: true,
                sectionWallLightIntensity: 0.72,
                sectionWallLightHeight: 54,
                sectionWallLightSpread: 72,
                sectionWallLightSoftness: 28,
                sectionWallLightX: 74,
                sectionWallLightY: 56,
                sectionWallLightYOffset: -28,
                sectionWallLightTextProtect: 0.72,
                sectionWallLightColourInfluence: 0.48,
                sectionWallLightColorMode: 'colourway',
                sectionWallLightWarmColor: '#d0a04f',
                sectionWallLightCoolColor: '#7b58d6',
                sectionArtEnabled: true,
                sectionWallTexturePreset: 'subtle-fibre',
                sectionWallTextureSrc: '/holding-wall-subtle-fibre.svg',
                sectionArtOpacity: 0.32,
                sectionArtTint: '#a58a65',
                sectionArtBlendMode: 'soft-light',
                sectionArtScale: 118,
                sectionWallTextureRepeatMode: 'cover',
                sectionWallTextureOpacity: 0.82,
                sectionWallTextureBrightness: 1.42,
                sectionWallTextureContrast: 1.24,
                sectionWallDepthEnabled: true,
                sectionWallDepthStrength: 0.38,
                sectionWallHighlightResponse: 0.62,
                sectionWallShadowResponse: 0.38,
                sectionWallLightTextureReveal: 0.54,
                sectionWallTextureReliefSoftness: 16,
                sectionFloorEnvironmentReflectionMode: 'consolidated',
                sectionFloorEnvironmentReflectionEnabled: true,
                sectionFloorArtReflectionEnabled: true,
                sectionFloorArtReflectionOpacity: 0.36,
                sectionFloorArtReflectionBlur: 7,
                sectionFloorArtReflectionDepth: 58,
                sectionFloorArtReflectionFade: 0.92,
                sectionReflectionForegroundVeilEnabled: true,
                sectionReflectionForegroundVeilOpacity: 0.16,
                sectionReflectionForegroundVeilBlur: 16,
                sectionReflectionForegroundVeilDepth: 34,
                sectionReflectionForegroundVeilStart: 0,
                sectionReflectionForegroundVeilSoftness: 0.72,
                legacySceneSymbolsEnabled: true,
                sectionSymbolRenderMode: 'baked-overlay',
                sectionSymbolFamilyPreset: 'bgg-premium',
                sectionSymbolBakedOverlaySrc: '/BGG.svg',
                sectionSymbolOverlaySrc: '/BGG.svg',
                sectionSymbolOpacity: 0.92,
                sectionSymbolScale: 132,
                sectionSymbolBlendMode: 'screen',
                sectionSymbolEffectPreset: 'flat',
                sectionSymbolEffectStrength: 0.2,
                sectionSymbolEffectDistance: 0.9,
                sectionSymbolEffectSoftness: 1.8,
                sectionSymbolLineWeight: 1.2,
                sectionSymbolEffectHighlightColor: '#f1d69a',
                sectionSymbolEffectShadowColor: '#130b05',
                sectionSymbolEffectOuterGlowColor: '#b9823c',
                sectionSymbolEffectOuterGlowOpacity: 0.2,
                sectionSymbolEffectLightAngle: 315,
                sectionSymbolFloorReflectionAmount: 0.1,
                sectionWallTextureScale: 168,
                sectionArtX: 62,
                sectionArtY: 39,
                sectionArtVignette: 0.82,
                sectionSceneFloorBaseFillEnabled: false,
                sectionFloorSpillHorizonBandEnabled: true,
                sectionColourwayFloorTailEnabled: false,
                sectionBacklightFloorSheenEnabled: true,
                sectionWallLightFloorSpillEnabled: true,
                responsiveSceneOverrides: {
                  mobile: {
                    sectionSceneHorizonY: 88,
                    sectionBacklightYOffset: 0,
                    sectionBacklightX: 50,
                    sectionWallLightYOffset: -24,
                    sectionWallLightX: 50,
                    sectionArtX: 54,
                    sectionArtY: 44,
                  },
                },
                publicPipelineOverrides: {
                  ...DEFAULT_INTRO_VIEWER_PIPELINE_OVERRIDES,
                  cameraTargetMode: 'fixed',
                  fixedCameraTarget: [0, 0, 0],
                  cameraTargetOffset: [-0.24, 0.42, 2.25],
                  modelScale: 0.37,
                  modelPosition: [4.2, -4.55, 0],
                  reflectionCloneEnabled: false,
                },
                responsivePipelineOverrides: {
                  tablet: {
                    cameraTargetMode: 'fixed',
                    fixedCameraTarget: [0, 0, 0],
                    cameraTargetOffset: [-0.18, 0.35, 2.25],
                    modelScale: 0.33,
                    modelPosition: [0, -2.75, 0],
                    reflectionCloneEnabled: false,
                  },
                  mobile: {
                    cameraTargetMode: 'fixed',
                    fixedCameraTarget: [0, 0, 0],
                    cameraTargetOffset: [-0.1, 0.3, 2.2],
                    modelScale: 0.33,
                    modelPosition: [0, -2.6, 0],
                    reflectionCloneEnabled: false,
                  },
                },
              },
              style: {
                zIndex: 20,
              },
            },
            {
              id: 'holding-symbol-motif',
              type: 'decorative-asset',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-symbol-motif'],
              responsiveLayout: holdingResponsiveLayout('holding-symbol-motif'),
              content: decorativeAssetPresetToBlockContent(holdingSymbolMotifPreset),
              style: {
                ...decorativeAssetPresetToBlockStyle(holdingSymbolMotifPreset),
                opacity: 0,
                blendMode: 'screen',
                blur: 0,
                zIndex: 8,
                builderLocked: true,
              },
            },
            {
              id: 'holding-logo',
              type: 'text',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-logo'],
              responsiveLayout: holdingResponsiveLayout('holding-logo'),
              content: {
                text: 'ED',
                presetKey: 'caption',
              },
              style: {
                fontFamilyKey: 'playfair',
                fontSize: 34,
                fontSizeDesktop: 34,
                fontSizeMobile: 24,
                fontWeight: 500,
                lineHeight: 1,
                textColor: '#d5b476',
                textOpacity: 0.96,
                letterSpacing: -0.04,
                textAlign: 'left',
                zIndex: 100,
              },
            },
            {
              id: 'holding-eyebrow-divider',
              type: 'decorative-asset',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-eyebrow-divider'],
              responsiveLayout: holdingResponsiveLayout('holding-eyebrow-divider'),
              content: decorativeAssetPresetToBlockContent(holdingEyebrowDividerPreset),
              style: {
                ...decorativeAssetPresetToBlockStyle(holdingEyebrowDividerPreset),
                opacity: 0.82,
                blendMode: 'screen',
                zIndex: 90,
              },
            },
            {
              id: 'holding-eyebrow',
              type: 'text',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-eyebrow'],
              responsiveLayout: holdingResponsiveLayout('holding-eyebrow'),
              content: {
                text: 'COMING SOON',
                presetKey: 'caption',
              },
              style: {
                fontFamilyKey: 'dmSans',
                textTransform: 'uppercase',
                letterSpacing: 0.22,
                textColor: '#e2c37e',
                textOpacity: 0.96,
                textAlign: 'center',
                fontSize: 13,
                fontSizeDesktop: 13,
                fontSizeMobile: 11,
                fontWeight: 500,
                zIndex: 101,
              },
            },
            {
              id: 'holding-title',
              type: 'text',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-title'],
              responsiveLayout: holdingResponsiveLayout('holding-title'),
              content: {
                text: 'Eco3Dcor',
                presetKey: 'heroTitle',
              },
              style: {
                fontFamilyKey: 'playfair',
                fontSize: 112,
                fontSizeDesktop: 112,
                fontSizeMobile: 52,
                fontWeight: 400,
                lineHeight: 0.95,
                textColor: '#f0d58a',
                textFillMode: 'gradient',
                textGradientStartColor: '#f6de96',
                textGradientEndColor: '#b78bed',
                textGradientAngle: 94,
                textShadow: {
                  x: 0,
                  y: 2,
                  blur: 10,
                  color: 'rgba(0,0,0,0.5)',
                },
                textAlign: 'left',
                zIndex: 100,
              },
            },
            {
              id: 'holding-body',
              type: 'text',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-body'],
              responsiveLayout: holdingResponsiveLayout('holding-body'),
              content: {
                text: 'A new home for our sculptural vases is taking shape. Fresh collections, event dates, and story-led designs will be unveiled here soon.',
                presetKey: 'body',
              },
              style: {
                fontFamilyKey: 'dmSans',
                fontSize: 18,
                fontSizeDesktop: 18,
                fontSizeMobile: 15,
                lineHeight: 1.55,
                textColor: '#e0c989',
                textOpacity: 0.94,
                textAlign: 'left',
                zIndex: 100,
              },
            },
            {
              id: 'holding-instagram',
              type: 'button',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-instagram'],
              responsiveLayout: holdingResponsiveLayout('holding-instagram'),
              content: {
                label: 'INSTAGRAM',
                actionMode: 'advanced',
                actionKind: 'navigation',
                advancedHref: 'https://www.instagram.com/eco3dcor',
                size: 'md',
                style: 'outline',
                icon: 'instagram',
                iconPosition: 'leading',
              },
              style: {
                backgroundColor: '#000000',
                backgroundOpacity: 0,
                borderColor: '#a98752',
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 10,
                sizingMode: 'fit',
                visualHeight: 36,
                alignX: 'center',
                alignY: 'center',
                paddingX: 10,
                paddingY: 0,
                textColor: '#c9a766',
                fontFamilyKey: 'dmSans',
                fontSize: 9,
                fontSizeDesktop: 9,
                fontSizeMobile: 9,
                fontWeight: 500,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                hover: {
                  backgroundColor: '#d6b36d',
                  backgroundOpacity: 0.055,
                  borderColor: '#d6b36d',
                  textColor: '#f2e7ca',
                  boxShadow: {
                    x: 0,
                    y: 0,
                    blur: 12,
                    spread: 0,
                    color: 'rgba(214, 179, 109, 0.14)',
                  },
                },
              },
            },
            {
              id: 'holding-email',
              type: 'button',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-email'],
              responsiveLayout: holdingResponsiveLayout('holding-email'),
              content: {
                label: 'INFO@ECO3DCOR.COM',
                actionMode: 'advanced',
                actionKind: 'navigation',
                advancedHref: 'mailto:info@eco3dcor.com',
                size: 'md',
                style: 'outline',
                icon: 'mail',
                iconPosition: 'leading',
              },
              style: {
                backgroundColor: '#000000',
                backgroundOpacity: 0,
                borderColor: '#a98752',
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 12,
                sizingMode: 'fit',
                visualHeight: 38,
                alignX: 'center',
                alignY: 'center',
                paddingX: 12,
                paddingY: 0,
                textColor: '#c9a766',
                fontFamilyKey: 'dmSans',
                fontSize: 10,
                fontSizeDesktop: 10,
                fontSizeMobile: 10,
                fontWeight: 500,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                hover: {
                  backgroundColor: '#d6b36d',
                  backgroundOpacity: 0.055,
                  borderColor: '#d6b36d',
                  textColor: '#f2e7ca',
                  boxShadow: {
                    x: 0,
                    y: 0,
                    blur: 12,
                    spread: 0,
                    color: 'rgba(214, 179, 109, 0.14)',
                  },
                },
              },
            },
            {
              id: 'holding-facebook',
              type: 'button',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-facebook'],
              responsiveLayout: holdingResponsiveLayout('holding-facebook'),
              content: {
                label: 'FACEBOOK',
                actionMode: 'advanced',
                actionKind: 'navigation',
                advancedHref: 'https://www.facebook.com/Eco3Dcor/',
                size: 'md',
                style: 'outline',
                icon: 'facebook',
                iconPosition: 'leading',
              },
              style: {
                backgroundColor: '#000000',
                backgroundOpacity: 0,
                borderColor: '#a98752',
                borderWidth: 1,
                borderStyle: 'solid',
                borderRadius: 10,
                sizingMode: 'fit',
                visualHeight: 36,
                alignX: 'center',
                alignY: 'center',
                paddingX: 10,
                paddingY: 0,
                textColor: '#c9a766',
                fontFamilyKey: 'dmSans',
                fontSize: 9,
                fontSizeDesktop: 9,
                fontSizeMobile: 9,
                fontWeight: 500,
                letterSpacing: 1.2,
                textTransform: 'uppercase',
                hover: {
                  backgroundColor: '#d6b36d',
                  backgroundOpacity: 0.055,
                  borderColor: '#d6b36d',
                  textColor: '#f2e7ca',
                  boxShadow: {
                    x: 0,
                    y: 0,
                    blur: 12,
                    spread: 0,
                    color: 'rgba(214, 179, 109, 0.14)',
                  },
                },
              },
            },
            {
              id: 'holding-footer-icon',
              type: 'decorative-asset',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-footer-icon'],
              responsiveLayout: holdingResponsiveLayout('holding-footer-icon'),
              content: decorativeAssetPresetToBlockContent(holdingFooterIconPreset),
              style: {
                ...decorativeAssetPresetToBlockStyle(holdingFooterIconPreset),
                opacity: 0.78,
                blendMode: 'screen',
                zIndex: 100,
              },
            },
            {
              id: 'holding-footer',
              type: 'text',
              layout: HOLDING_PAGE_BLOCK_LAYOUTS.desktop['holding-footer'],
              responsiveLayout: holdingResponsiveLayout('holding-footer'),
              content: {
                text: 'STORY-DRIVEN SCULPTURAL VASES INSPIRED BY FOLKLORE AND MYTHOLOGY\nSUSTAINABLE. TIMELESS. UNIQUE.',
                presetKey: 'caption',
              },
              style: {
                fontFamilyKey: 'dmSans',
                fontSize: 11,
                fontSizeDesktop: 11,
                fontSizeMobile: 10,
                fontWeight: 500,
                lineHeight: 1.5,
                letterSpacing: 0.18,
                textColor: '#d0b073',
                textOpacity: 0.92,
                textAlign: 'left',
                zIndex: 100,
              },
            },
          ],
        },
      },
    },
  ],
};

const HOLDING_VIEWER_BLOCK =
  DEFAULT_HOLDING_LAYOUT_CONTENT.sections[0]?.settings?.canvas?.blocks?.find(
    (entry) => entry.id === 'holding-viewer'
  ) ?? null;

export function getHoldingViewerFramingDefaults(
  breakpoint: 'desktop' | 'tablet' | 'mobile'
): Record<string, unknown> {
  const content =
    HOLDING_VIEWER_BLOCK?.content && typeof HOLDING_VIEWER_BLOCK.content === 'object'
      ? (HOLDING_VIEWER_BLOCK.content as Record<string, unknown>)
      : null;
  if (!content) return {};

  if (breakpoint === 'desktop') {
    const overrides = content.publicPipelineOverrides;
    return overrides && typeof overrides === 'object' && !Array.isArray(overrides)
      ? { ...(overrides as Record<string, unknown>) }
      : {};
  }

  const responsiveRoot = content.responsivePipelineOverrides;
  if (!responsiveRoot || typeof responsiveRoot !== 'object' || Array.isArray(responsiveRoot)) {
    return {};
  }
  const breakpointOverrides = (responsiveRoot as Record<string, unknown>)[breakpoint];
  return breakpointOverrides && typeof breakpointOverrides === 'object' && !Array.isArray(breakpointOverrides)
    ? { ...(breakpointOverrides as Record<string, unknown>) }
    : {};
}

export function getHoldingViewerSceneDefaults(
  breakpoint: 'desktop' | 'tablet' | 'mobile'
): Record<string, unknown> {
  const content =
    HOLDING_VIEWER_BLOCK?.content && typeof HOLDING_VIEWER_BLOCK.content === 'object'
      ? (HOLDING_VIEWER_BLOCK.content as Record<string, unknown>)
      : null;
  if (!content) return {};

  if (breakpoint === 'desktop') {
    const merged: Record<string, unknown> = {};
    for (const key of [
      'sectionSceneHorizonY',
      'sectionBacklightY',
      'sectionBacklightYOffset',
      'sectionWallLightYOffset',
      'sectionBacklightX',
      'sectionWallLightX',
      'sectionArtX',
      'sectionArtY',
    ]) {
      if (content[key] !== undefined) merged[key] = content[key];
    }
    return merged;
  }

  const responsiveRoot = content.responsiveSceneOverrides;
  if (!responsiveRoot || typeof responsiveRoot !== 'object' || Array.isArray(responsiveRoot)) {
    return {};
  }
  const breakpointOverrides = (responsiveRoot as Record<string, unknown>)[breakpoint];
  return breakpointOverrides && typeof breakpointOverrides === 'object' && !Array.isArray(breakpointOverrides)
    ? { ...(breakpointOverrides as Record<string, unknown>) }
    : {};
}

export function applyHoldingMobileStarterLayout(
  content: HomepageLayoutContent
): HomepageLayoutContent {
  const next = JSON.parse(JSON.stringify(content)) as HomepageLayoutContent;
  const section = next.sections.find((entry) => entry.id === 'holding-stage');
  const canvas = section?.settings?.canvas;
  if (!section || !canvas?.blocks) return next;

  canvas.minRows = 22;

  for (const block of canvas.blocks) {
    const mobileLayout = HOLDING_PAGE_BLOCK_LAYOUTS.mobile[block.id];
    if (mobileLayout) {
      block.responsiveLayout = {
        ...block.responsiveLayout,
        mobile: { ...mobileLayout },
      };
    }

    if (block.id === 'holding-viewer' && block.content && typeof block.content === 'object') {
      const viewerContent = block.content as Record<string, unknown>;
      const defaultViewer = DEFAULT_HOLDING_LAYOUT_CONTENT.sections[0]?.settings?.canvas?.blocks?.find(
        (entry) => entry.id === 'holding-viewer'
      );
      const defaultMobilePipeline =
        defaultViewer?.content &&
        typeof defaultViewer.content === 'object' &&
        (defaultViewer.content as Record<string, unknown>).responsivePipelineOverrides &&
        typeof (defaultViewer.content as Record<string, unknown>).responsivePipelineOverrides === 'object'
          ? ((defaultViewer.content as Record<string, unknown>).responsivePipelineOverrides as Record<
              string,
              unknown
            >).mobile
          : undefined;
      if (defaultMobilePipeline) {
        viewerContent.responsivePipelineOverrides = {
          ...(viewerContent.responsivePipelineOverrides as Record<string, unknown> | undefined),
          mobile: defaultMobilePipeline,
        };
      }
      const defaultMobileScene =
        defaultViewer?.content &&
        typeof defaultViewer.content === 'object' &&
        (defaultViewer.content as Record<string, unknown>).responsiveSceneOverrides &&
        typeof (defaultViewer.content as Record<string, unknown>).responsiveSceneOverrides === 'object'
          ? ((defaultViewer.content as Record<string, unknown>).responsiveSceneOverrides as Record<
              string,
              unknown
            >).mobile
          : undefined;
      if (defaultMobileScene) {
        viewerContent.responsiveSceneOverrides = {
          ...(viewerContent.responsiveSceneOverrides as Record<string, unknown> | undefined),
          mobile: defaultMobileScene,
        };
      }
    }

    if (block.id === 'holding-body' && block.content && typeof block.content === 'object') {
      (block.content as Record<string, unknown>).text =
        'A new home for our sculptural vases is taking shape. Fresh collections, event dates, and story-led designs will be unveiled here soon.';
    }

    if (block.id === 'holding-footer' && block.content && typeof block.content === 'object') {
      (block.content as Record<string, unknown>).text =
        'STORY-DRIVEN SCULPTURAL VASES INSPIRED BY FOLKLORE AND MYTHOLOGY\nSUSTAINABLE. TIMELESS. UNIQUE.';
    }
  }

  return next;
}

let ensureHoldingPagePromise: Promise<void> | null = null;

export async function ensureHoldingPageLayout() {
  if (ensureHoldingPagePromise) return ensureHoldingPagePromise;

  ensureHoldingPagePromise = (async () => {
    const pagesRes = await listPageLayoutsForAdmin();
    if (pagesRes.error) throw pagesRes.error;

    const exists = (pagesRes.data ?? []).some((row) => row.page_key === HOLDING_PAGE_KEY);
    if (exists) return;

    const { error } = await createPageLayout({
      pageKey: HOLDING_PAGE_KEY,
      title: HOLDING_PAGE_TITLE,
      draftContent: DEFAULT_HOLDING_LAYOUT_CONTENT,
      publishedContent: DEFAULT_HOLDING_LAYOUT_CONTENT,
    });

    if (error) {
      const message = String((error as any)?.message ?? '').toLowerCase();
      if (!message.includes('already exists') && !message.includes('duplicate')) {
        throw error;
      }
    }
  })();

  try {
    await ensureHoldingPagePromise;
  } finally {
    ensureHoldingPagePromise = null;
  }
}
